import React, { useEffect, useState } from 'react'
import {
	Box,
	Chip,
	CircularProgress,
	FormControl,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { dsColorNew, dataColors } from '@palette'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, adminHintSx, rowToneSx } from '../_shared/ui'

/**
 * D3 — Channel Mapping admin.
 *
 * Lists every dim_booking_channel row for the tenant alongside its
 * trailing-365-days booking volume and revenue. Operator picks the
 * canonical channel_type (Direct / B2B / OTA / External / Other) from
 * an inline dropdown; PATCH fires on change. Once mapped, downstream
 * dashboards can slice by channel_type.
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n)

const MB_VIOLET = dataColors.violet500

const TYPE_COLOR = {
	Direct:   'success',
	B2B:      'info',
	OTA:      'warning',
	External: 'secondary',
	Other:    'default',
}
// The scope gives the soft tones of the standard colours; External keeps its own violet, softened
const TYPE_SX = {
	secondary: { backgroundColor: alpha(MB_VIOLET, 0.12), color: dataColors.violet700 },
}

export default function ChannelMappingPage({ embedded = false }) {
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [savingId, setSavingId] = useState(null)
	const [savedAt, setSavedAt] = useState({})  // channel_id → ts

	const fetchAll = () => {
		setLoading(true); setError(null)
		fetch('/api/my-business/admin/channels', { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}

	useEffect(() => { fetchAll() }, [])

	const updateType = (channelId, newType) => {
		setSavingId(channelId)
		fetch(`/api/my-business/admin/channels/${channelId}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ channel_type: newType || null }),
		})
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(() => {
				setData((prev) => ({
					...prev,
					channels: prev.channels.map(c =>
						c.id === channelId ? { ...c, channel_type: newType || null } : c
					),
				}))
				setSavedAt((prev) => ({ ...prev, [channelId]: Date.now() }))
			})
			.catch(e => setError(e.message))
			.finally(() => setSavingId(null))
	}

	const unmappedCount = data?.channels?.filter(c => !c.channel_type).length ?? 0
	const totalCount    = data?.channels?.length ?? 0
	const totalBookings = data?.channels?.reduce((s, c) => s + c.bookings, 0) ?? 0
	const unmappedBookings = data?.channels
		?.filter(c => !c.channel_type)
		.reduce((s, c) => s + c.bookings, 0) ?? 0

	const body = (
		<>
			<AdminProgress active={loading}/>

			<Box>
				{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}

				{loading && !data && <AppResultsSkeleton columns={4}/>}

				{data && (
					<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
						<AppCardTitle icon="shuffle" title={MY_BUSINESS_ROUTES.CHANNEL_ADMIN_TITLE}/>

						{/* Summary: how many channels are mapped and what still flows through the unmapped ones */}
						<Box display="flex" alignItems="center" gap={3} flexWrap="wrap" sx={{ mt: 1.5, mb: 2 }}>
							<Box>
								<Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: dsColorNew.textTitle }}>
									{totalCount - unmappedCount} <Box component="span"
									                                   sx={{ fontSize: 15, color: dsColorNew.textMuted, fontWeight: 500 }}>
										/ {totalCount} channels mapped
									</Box>
								</Typography>
								<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.25 }}>
									{unmappedCount > 0
										? `${unmappedCount} channel${unmappedCount > 1 ? 's' : ''} still unmapped`
										: 'All channels mapped'}
								</Typography>
							</Box>
							{unmappedBookings > 0 && (
								<Box>
									<Typography sx={{
										fontSize: 13, px: 1.25, py: 0.5, borderRadius: '10px',
										backgroundColor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}`,
									}}>
										<Box component="span" sx={{ fontWeight: 700, color: dsColorNew.noteAmber }}>
											{fmtNum(unmappedBookings)}
										</Box>
										<Box component="span" sx={{ color: dsColorNew.textDark }}>
											{' '}bookings (
											{Math.round(unmappedBookings / totalBookings * 100) || 0}% of total)
											still flow through unmapped channels
										</Box>
									</Typography>
								</Box>
							)}
						</Box>

						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Channel</TableCell>
										<TableCell align="right">Bookings (12m)</TableCell>
										<TableCell align="right">Revenue (12m)</TableCell>
										<TableCell>Channel Type</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{data.channels.map((c) => {
										const justSaved = savedAt[c.id] && Date.now() - savedAt[c.id] < 1500
										return (
											<TableRow key={c.id} hover
											          sx={rowToneSx({ saved: justSaved })}>
												<TableCell>
													<Box sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{c.name}</Box>
													{c.bookings === 0 && (
														<Box sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
															no bookings in window
														</Box>
													)}
												</TableCell>
												<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
													{fmtNum(c.bookings)}
												</TableCell>
												<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
													{fmtEur(c.revenue)}
												</TableCell>
												<TableCell sx={{ width: 200 }}>
													<Box display="flex" alignItems="center" gap={1}>
														<FormControl size="small" sx={{ minWidth: 130 }}>
															<Select value={c.channel_type || ''}
															        displayEmpty
															        disabled={savingId === c.id}
															        onChange={(e) => updateType(c.id, e.target.value)}
															        renderValue={(v) => v
																        ? <Chip size="small"
																                color={TYPE_COLOR[v] || 'default'}
																                label={v}
																                sx={{ fontWeight: 700, ...TYPE_SX[TYPE_COLOR[v]] }}/>
																        : <Box sx={{ color: dsColorNew.textSubtle,
																                     fontStyle: 'italic',
																                     fontSize: 13 }}>
																            unmapped
																          </Box>}>
																<MenuItem value="">
																	<em>unmapped</em>
																</MenuItem>
																{(data.types || []).map(t => (
																	<MenuItem key={t} value={t}>{t}</MenuItem>
																))}
															</Select>
														</FormControl>
														{savingId === c.id && <CircularProgress size={16}/>}
													</Box>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>

						<Typography sx={{ ...adminHintSx, mt: 1.5, '& strong': { color: dsColorNew.textDark, fontWeight: 600 } }}>
							<strong>Direct</strong>: walk-in or own website ·
							<strong> B2B</strong>: agencies, tour operators with portal access ·
							<strong> OTA</strong>: Booking.com, Expedia, etc. ·
							<strong> External</strong>: third-party systems / integrations ·
							<strong> Other</strong>: anything not captured above.
						</Typography>
					</Paper>
				)}
			</Box>
		</>
	)

	if (embedded) return body
	return (
		<MbPage title={MY_BUSINESS_ROUTES.CHANNEL_ADMIN_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.CHANNEL_ADMIN_SUBTITLE}>
			{body}
		</MbPage>
	)
}
